/**
 * Institution Testimonials Component
 */

import React from 'react';
import { Grid, Column } from '@carbon/react';
import { Star, StarFilled, Quotes } from '@carbon/icons-react';
import type { Testimonial } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionTestimonialsProps {
  testimonials: Testimonial[];
  institutionName: string;
}

const InstitutionTestimonials: React.FC<InstitutionTestimonialsProps> = ({ testimonials, institutionName }) => {
  const renderRating = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(i <= rating ? <StarFilled key={i} size={16} /> : <Star key={i} size={16} />);
    }
    return <div className="testimonial-rating">{stars}</div>;
  };

  return (
    <section className="institution-testimonials-section">
      <Grid narrow>
        <Column lg={16}>
          <h2 className="testimonials-title">What Our Community Says</h2>
          <p className="testimonials-subtitle">
            Real experiences from educators, students, and parents at {institutionName}
          </p>
        </Column>

        {testimonials.slice(0, 6).map(testimonial => (
          <Column key={testimonial.id} lg={8} md={4} sm={4}>
            <div className="testimonial-card">
              <Quotes size={24} className="quote-icon" />
              <p className="testimonial-quote">{testimonial.quote}</p>
              {testimonial.rating && renderRating(testimonial.rating)}
              <div className="testimonial-author">
                {testimonial.imageUrl && (
                  <img src={testimonial.imageUrl} alt={testimonial.author} className="author-image" />
                )}
                <div className="author-info">
                  <p className="author-name">{testimonial.author}</p>
                  <p className="author-role">
                    {testimonial.role}
                    {testimonial.institution && `, ${testimonial.institution}`}
                  </p>
                </div>
              </div>
            </div>
          </Column>
        ))}
      </Grid>

      <style>{`
        .institution-testimonials-section {
          padding: var(--cds-spacing-11) 0;
          background-color: var(--cds-layer-01);
        }

        .testimonials-title {
          font-size: var(--cds-display-01-font-size);
          font-weight: var(--cds-display-01-font-weight);
          margin-bottom: var(--cds-spacing-05);
          color: var(--cds-text-primary);
          text-align: center;
        }

        .testimonials-subtitle {
          font-size: var(--cds-body-long-02-font-size);
          color: var(--cds-text-secondary);
          text-align: center;
          margin-bottom: var(--cds-spacing-10);
        }

        .testimonial-card {
          background-color: var(--cds-background);
          padding: var(--cds-spacing-08);
          height: 100%;
          margin-bottom: var(--cds-spacing-08);
          display: flex;
          flex-direction: column;
          border: 1px solid var(--cds-border-subtle);
          transition: box-shadow var(--cds-duration-fast-02) var(--cds-ease-out);
          border-radius: var(--cds-border-radius);
        }

        .testimonial-card:hover {
          box-shadow: var(--cds-shadow);
          border-color: var(--cds-border-interactive);
        }

        .quote-icon {
          color: var(--cds-interactive-01, #8a3ffc);
          margin-bottom: 1rem;
        }

        .testimonial-quote {
          font-size: 1rem;
          line-height: 1.6;
          color: var(--cds-text-primary);
          margin-bottom: 1rem;
          flex-grow: 1;
          font-style: italic;
        }

        .testimonial-rating {
          display: flex;
          gap: 0.25rem;
          margin-bottom: 1rem;
          color: var(--cds-interactive-02, #ffc700);
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-top: auto;
          padding-top: 1rem;
          border-top: 1px solid var(--cds-border-subtle);
        }

        .author-image {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .author-info {
          flex-grow: 1;
        }

        .author-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--cds-text-primary);
          margin-bottom: 0.25rem;
        }

        .author-role {
          font-size: 0.75rem;
          color: var(--cds-text-secondary);
        }
      `}</style>
    </section>
  );
};

export default InstitutionTestimonials;
